import {computed, inject, Injectable, Signal, signal, WritableSignal} from "@angular/core";
import {firstValueFrom, ReplaySubject, Subject} from "rxjs";
import {AuthService, EntityData, LocalConfigurationService, LoginType} from "@fedelta-pos/data-client";
import {Local} from "./config";
import {BeaconData, BeaconOrderType, BeaconSearchModel} from '../beacon/beacon-search.model';
import {IdentityModel} from '../identity/identity.model';
import {StoreData} from '../store/store-search.model';
import {OrderType} from '../order/order';
import {AppData, AppEntityModel, AppLoginAuthenticationMethod, AppLoginIdentificationMethod} from './app-entity.model';
import {OrderingContextModel} from '../ordering-context/ordering-context.model';
import {DeferredInjectable} from '../deferred-injection.service';
import {AppVersionHolder} from './version';
import {formatDateForDisplay} from '../utils/date-time-utils';
import {TranslateService} from '@ngx-translate/core';

// noinspection JSUnusedGlobalSymbols
export enum AppDeviceType {
    SERVICE_APP = "SERVICE_APP",
}

export enum ActorType {
    CONSUMER = "CONSUMER",
    WAITER = "WAITER"
}

export class ParamConfig {
    parentAppURL: string;
    store: number;
    exclusiveStore: number;
    stores: number[];
    exclusiveStores: number[];
    storeGroup: string;
    exclusiveStoreGroup: string;
    orderType: OrderType;
    tableLookupCode: string;
    tableNumber: string; // deprecated - retained for backwards compatibility with custom beacons or links
    tableOnlyMode: boolean;
    pickupOnlyMode: boolean;
    deliveryOnlyMode: boolean;
    showExitButton: boolean;
    forceMobile: boolean;
    scanId: string;
    beaconId: string;
    unknownBeacon: boolean;
    beaconLookupFailed: boolean;
    beaconScanTime: number;
    dealCode: string;
    deviceId: string;
    deviceType: AppDeviceType;
    deviceSecret: string;
    actorType: ActorType = ActorType.CONSUMER;
    scanType: string;
    defaultLocalDateTime: string;
    useStoreCoords: boolean;
    latitude: number;
    longitude: number;
    traceNav: boolean;
    noAutoScroll: boolean;
    forcePay: string;
    forceTyroWalletButtons: boolean;
    forcePaymentActivationError: boolean;
    allowConsumerPaymentTypes: boolean;

    /**
     * Creates a config for an external store route
     */
    cloneForExternalStore(): ParamConfig {
        const config: ParamConfig = new ParamConfig();
        config.exclusiveStore = this.exclusiveStore;
        config.exclusiveStoreGroup = this.exclusiveStoreGroup;
        config.exclusiveStores = this.exclusiveStores;
        config.orderType = this.orderType;
        config.tableLookupCode = this.tableLookupCode;
        config.tableOnlyMode = this.tableOnlyMode;
        config.deliveryOnlyMode = this.deliveryOnlyMode;
        config.pickupOnlyMode = this.pickupOnlyMode;
        config.showExitButton = this.showExitButton;
        config.forceMobile = this.forceMobile;
        config.beaconId = this.beaconId;
        config.beaconScanTime = this.beaconScanTime;
        config.dealCode = this.dealCode;
        config.deviceId = this.deviceId;
        config.deviceType = this.deviceType;
        config.deviceSecret = this.deviceSecret;
        config.actorType = this.actorType;
        config.scanType = this.scanType;
        config.defaultLocalDateTime = this.defaultLocalDateTime;
        config.useStoreCoords = this.useStoreCoords;
        config.latitude = this.latitude;
        config.longitude = this.longitude;
        config.traceNav = this.traceNav;
        config.noAutoScroll = this.noAutoScroll;
        config.forcePay = this.forcePay;
        config.forceTyroWalletButtons = this.forceTyroWalletButtons;
        config.forcePaymentActivationError = this.forcePaymentActivationError;
        config.allowConsumerPaymentTypes = this.allowConsumerPaymentTypes;
        return config;
    }

    static standardiseLocalDateTime(localDateTime: string): string {
        return !localDateTime ? undefined
            : localDateTime.length === 10 ? localDateTime + "T00:00:00"
                : localDateTime.length === 13 ? localDateTime + ":00:00"
                    : localDateTime.length === 16 ? localDateTime + ":00"
                        : localDateTime;
    }
}

@Injectable({providedIn: "root"})
export class ConfigurationModel implements DeferredInjectable {

    /**
     * Notifies the device id - used for initial auth config.
     */
    public static readonly deviceId$: Subject<string> = new ReplaySubject(1);

    /**
     * Notifies the actor type - used for initial auth config.
     */
    public static readonly actorType$: Subject<ActorType> = new ReplaySubject(1);

    private appEntityModel: AppEntityModel = inject(AppEntityModel);
    private beaconSearchModel: BeaconSearchModel = inject(BeaconSearchModel);
    private authService: AuthService = inject(AuthService);
    private localConfigurationService: LocalConfigurationService = inject(LocalConfigurationService);
    private translate: TranslateService = inject(TranslateService);
    private orderingContextModel: OrderingContextModel;
    private identityModel: IdentityModel;

    public readonly app: WritableSignal<AppData | undefined> = signal<AppData>(undefined);
    private readonly actorType: WritableSignal<ActorType> = signal<ActorType>(undefined);
    private readonly _showExitButton: WritableSignal<boolean> = signal<boolean>(undefined);
    public readonly showExitButton: Signal<boolean> = this._showExitButton.asReadonly();
    private readonly _noAutoScroll: WritableSignal<boolean> = signal<boolean>(undefined);
    public readonly noAutoScroll: Signal<boolean> = this._noAutoScroll.asReadonly();
    private readonly _defaultDateTime: WritableSignal<Date> = signal<Date>(undefined);
    private readonly _appName: WritableSignal<string> = signal<string>(undefined);
    public readonly appName: Signal<string> = this._appName.asReadonly();
    private readonly _clientId: WritableSignal<string> = signal<string>(undefined);
    public readonly clientId: Signal<string> = this._clientId.asReadonly();
    private readonly _devMode: WritableSignal<boolean> = signal<boolean>(undefined);
    public readonly devMode: Signal<boolean> = this._devMode.asReadonly();
    private readonly _devOrDemoMode: WritableSignal<boolean> = signal<boolean>(undefined);
    public readonly devOrDemoMode: Signal<boolean> = this._devOrDemoMode.asReadonly();
    private readonly _staging: WritableSignal<boolean> = signal<boolean>(undefined);
    public readonly staging: Signal<boolean> = this._staging.asReadonly();
    // If populated, then the App is in preview mode. The token is sent with the appId when retrieving the App to get preview data
    private readonly _previewToken: WritableSignal<string> = signal<string>(undefined);
    public readonly previewToken: Signal<string> = this._previewToken.asReadonly();
    // Returns the current date truncated to the day (time set to 00:00:00).
    private readonly _today: WritableSignal<Date> = signal<Date>(this.getTodayDate());
    public readonly today: Signal<Date> = this._today.asReadonly();

    public readonly production: Signal<boolean> = computed<boolean>(() => !this.devMode() && !this.staging());
    public readonly timeTestMode: Signal<boolean> = computed<boolean>(() => !!this._defaultDateTime());
    public readonly isWaiterActor: Signal<boolean> = computed<boolean>(() => this.actorType() === ActorType.WAITER);
    public readonly isConsumerActor: Signal<boolean> = computed<boolean>(() => this.actorType() === ActorType.CONSUMER);
    public readonly requireWaiterName: Signal<boolean> = computed<boolean>(() => this.isWaiterActor() && this.app()?.requireWaiterName);
    public readonly showOrderNotes: Signal<boolean> = computed<boolean>(() => this.isWaiterActor() || this.app()?.showNotes);
    public readonly showOrderItemNotes: Signal<boolean> = computed<boolean>(() => this.isWaiterActor() && this.orderingContextModel.currentStore()?.allowOrderItemNotes);
    public readonly canSendOrderReceivedEmail: Signal<boolean> = computed<boolean>(() => this.app()?.sendOrderReceivedEmail && !!this.app()?.orderReceivedEmail.id);
    public readonly allowMemberLogin: Signal<boolean> = computed(() => this.app()?.allowMemberLogin && this.isConsumerActor());
    public readonly supportsRegistration: Signal<boolean> = computed(() => this.app()?.supportsRegistration &&
        (this.app()!.loginIdentificationMethod === AppLoginIdentificationMethod.EMAIL || this.app()!.loginIdentificationMethod === AppLoginIdentificationMethod.MOBILE));
    public readonly loginIdentificationMethod: Signal<AppLoginIdentificationMethod> = computed(() => this.app()?.loginIdentificationMethod ?? AppLoginIdentificationMethod.EMAIL);
    public readonly loginAuthenticationMethod: Signal<AppLoginAuthenticationMethod> = computed(() => this.app()?.loginAuthenticationMethod ?? AppLoginAuthenticationMethod.PASSWORD);
    public readonly captureMemberDOB: Signal<boolean> = computed(() => this.app()?.captureMemberDOB);
    public readonly supportsForgotPassword: Signal<boolean> = computed(() => this.app()?.supportsForgotPassword);
    public readonly memberNoIsNumeric: Signal<boolean> = computed(() => this.app()?.memberNoIsNumeric);
    public readonly loginType: Signal<LoginType> = computed(() =>
        this.loginIdentificationMethod() === AppLoginIdentificationMethod.EMAIL ? LoginType.MEMBER_EMAIL
            : this.loginIdentificationMethod() === AppLoginIdentificationMethod.MOBILE ? LoginType.MEMBER_MOBILE
                : this.loginIdentificationMethod() === AppLoginIdentificationMethod.MEMBER_NO ? LoginType.MEMBER_NO
                    : this.loginIdentificationMethod() === AppLoginIdentificationMethod.MEMBER_NO_PLUS_MOBILE ? LoginType.MEMBER_NO_PLUS_MOBILE
                        : undefined);
    public readonly loginIdentityIsEmail: Signal<boolean> = computed(() => this.loginType() === LoginType.MEMBER_EMAIL);
    public readonly loginIdentityIsMobile: Signal<boolean> = computed(() => this.loginType() === LoginType.MEMBER_MOBILE);
    public readonly loginIdentityIsMemberNo: Signal<boolean> = computed(() => this.loginType() === LoginType.MEMBER_NO || this.loginType() === LoginType.MEMBER_NO_PLUS_MOBILE);
    public readonly loginRequiresSecondaryMobile: Signal<boolean> = computed(() => this.loginType() === LoginType.MEMBER_NO_PLUS_MOBILE);
    public readonly supportsAutomaticLogin: Signal<boolean> = computed(() => this.app()?.supportsAutomaticLogin && (this.loginIdentityIsMobile() || this.loginIdentityIsEmail()));

    public readonly relativeDateAndTime: Signal<(dateSt: string, timeSt: string) => string> = computed(() => {
        const todaySt: string = formatDateForDisplay(this.today(), false);
        const tomorrow = new Date(this.today());
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowSt: string = formatDateForDisplay(tomorrow, false);
        return (dateSt: string, timeSt: string): string =>
            timeSt + " " + (dateSt === todaySt ? this.translate.instant("woa.common.today")
                : dateSt === tomorrowSt ? this.translate.instant("woa.common.tomorrow")
                    : dateSt);
    });

    /**
     * If a phone number is available and either uses the expected country code or has no country code but looks like a mobile number, we assume it is valid for sending an SMS.
     */
    public readonly isValidMobileNumberForSms: Signal<(mobilePhone: string) => boolean> = computed<(mobilePhone: string) => boolean>(() => {
        const app: AppData = this.app();
        return (mobilePhone: string): boolean => {
            return (!mobilePhone || !mobilePhone.match("[+]?\\d{10,30}")) ? false
                : !mobilePhone.startsWith("+") ? (mobilePhone.startsWith("04") && mobilePhone.length == 10)
                    : app?.expectedCountryCode ? (mobilePhone.startsWith("+" + app?.expectedCountryCode + "4") && mobilePhone.length == (app?.expectedCountryCode.length + 10))
                        : (mobilePhone.charAt(mobilePhone.length - 9) == "4");
        };
    });

    private paramConfig$: Subject<ParamConfig> = new ReplaySubject(1);

    private appConfigParam: any;

    constructor() {
        console.log("--- ConfigurationModel");

        // Monitor for day changes and update the today signal when the day changes.
        // Checks every minute to handle midnight transitions and daylight savings changes.
        setInterval(() => {
            const currentToday: Date = this.getTodayDate();
            if (currentToday.getTime() !== this._today().getTime()) {
                this._today.set(currentToday);
            }
        }, 60000); // Check every minute
    }

    public inject(): void {
        this.orderingContextModel = inject(OrderingContextModel);
        this.identityModel = inject(IdentityModel);
    }

    /**
     * Loads flags from local config.
     * Parses the app URL to get config params.
     */
    public async init(): Promise<void> {

        const urlParams: URLSearchParams = new URLSearchParams(location.search);

        // Override appconfig.json settings from the appConfig param
        const appConfigParamStr: string = urlParams.get("appConfig");
        if (appConfigParamStr) {
            this.appConfigParam = JSON.parse(atob(appConfigParamStr));

            if (this.appConfigParam["appId"]) {
                this.localConfigurationService.saveNumber(Local.APP_ID, this.appConfigParam["appId"], true);
            }
            if (this.appConfigParam["appName"]) {
                this.localConfigurationService.saveNumber(Local.APP_NAME, this.appConfigParam["appName"], true);
            }
            if (this.appConfigParam["loginToken"]) {
                this.localConfigurationService.saveString(Local.LOGIN_TOKEN, this.appConfigParam["loginToken"], true);
            }
            if (this.appConfigParam["dataserverURL"]) {
                this.localConfigurationService.saveString(Local.DATASERVER_URL, this.appConfigParam["dataserverURL"], true);
            }
            if (this.appConfigParam["dataserverAppVersion"]) {
                AppVersionHolder.version = this.appConfigParam["dataserverAppVersion"];
            }
            if (this.appConfigParam["previewToken"]) {
                this._previewToken.set(this.appConfigParam["previewToken"]);
                // Logout so the app will log in using the new token
                this.authService.logout();
            }
        }

        // Load flags from local config
        const dataServerUrl: string = await this.localConfigurationService.getString(Local.DATASERVER_URL);
        this._appName.set(await this.localConfigurationService.getString(Local.APP_NAME));
        this._devMode.set(!dataServerUrl || dataServerUrl.indexOf("localhost") > 0 || dataServerUrl.indexOf(".dev.") > 0);
        this._devOrDemoMode.set(this._devMode() || await this.localConfigurationService.getBoolean(Local.DEMO));
        if (this._devMode()) {
            this._clientId.set("dev");
        } else if (dataServerUrl.startsWith("https://") && dataServerUrl.indexOf("chewzie.net") > 0) {
            let clientId: string = dataServerUrl.substring("https://".length).replace(/\..*/, "");
            const i: number = clientId.lastIndexOf("-");
            if (i >= 0) {
                clientId = clientId.substring(0, i);
                this._appName.set(clientId);
            }
            this._clientId.set(clientId);
        }
        this._staging.set(!this._devMode() && this._clientId() && (this._clientId().indexOf("-") > 0 || dataServerUrl.indexOf(".stage.") > 0));

        // Load config from URL
        let paramConfig: ParamConfig;
        if (urlParams.has("c")) {
            // base64 encoded JSON config from the beacon lookup service, e.g. {"tableNumber": 22, "stores": [1,2], "tableOnlyMode": true}
            paramConfig = Object.assign(new ParamConfig(), JSON.parse(atob(urlParams.get("c"))));
            paramConfig.defaultLocalDateTime = ParamConfig.standardiseLocalDateTime(paramConfig.defaultLocalDateTime);
            this.authService.setDefaultLocalDateTime(paramConfig.defaultLocalDateTime);

            // tableNumber is deprecated - retained for backwards compatibility with custom beacons or links
            if (paramConfig.tableNumber && !paramConfig.tableLookupCode) {
                paramConfig.tableLookupCode = paramConfig.tableNumber;
                paramConfig.tableNumber = undefined;
            }
        } else {
            // Cherry-pick config
            paramConfig = new ParamConfig();

            if (urlParams.has("id")) {
                paramConfig.beaconId = urlParams.get("id");
            }

            if (urlParams.has("sid")) {
                paramConfig.scanId = urlParams.get("sid");
            }

            if (urlParams.has("bst")) {
                paramConfig.beaconScanTime = parseInt(urlParams.get("bst"));
            }

            if (urlParams.has("parentAppURL")) {
                paramConfig.parentAppURL = urlParams.get("parentAppURL");
            }

            if (urlParams.has("scan")) {
                paramConfig.scanType = urlParams.get("scan");
            }

            if (urlParams.has("forcePay")) {
                paramConfig.forcePay = urlParams.get("forcePay");
            }
            paramConfig.forceTyroWalletButtons = urlParams.has("forceTyroWalletButtons");
            paramConfig.forcePaymentActivationError = urlParams.has("forcePaymentActivationError");
            paramConfig.allowConsumerPaymentTypes = urlParams.has("allowConsumerPaymentTypes");

            if (urlParams.has("defaultLocalDateTime")) {
                paramConfig.defaultLocalDateTime = ParamConfig.standardiseLocalDateTime(urlParams.get("defaultLocalDateTime"));
                this.authService.setDefaultLocalDateTime(paramConfig.defaultLocalDateTime);
            }

            if (urlParams.has("useStoreCoords")) {
                paramConfig.useStoreCoords = true;
            } else if (urlParams.has("latitude") && urlParams.has("longitude")) {
                paramConfig.latitude = parseInt(urlParams.get("latitude"));
                paramConfig.longitude = parseInt(urlParams.get("longitude"));
            }

            if (urlParams.has("exclusiveStore")) {
                paramConfig.exclusiveStore = parseInt(urlParams.get("exclusiveStore"));
            } else if (urlParams.has("singleStore")) { // backward compatibility
                paramConfig.exclusiveStore = parseInt(urlParams.get("singleStore"));
            } else {
                if (urlParams.has("store")) {
                    paramConfig.store = parseInt(urlParams.get("store"));
                }

                if (urlParams.has("exclusiveStoreGroup")) {
                    paramConfig.exclusiveStoreGroup = urlParams.get("exclusiveStoreGroup");
                } else if (urlParams.has("exclusiveStores")) {
                    paramConfig.exclusiveStores = [];
                    const stores: string[] = urlParams.get("exclusiveStores").split(",");
                    stores.forEach((s: string) => paramConfig.exclusiveStores.push(parseInt(s)));
                } else if (urlParams.has("storeGroup")) {
                    paramConfig.storeGroup = urlParams.get("storeGroup");
                } else if (urlParams.has("stores")) {
                    paramConfig.stores = [];
                    const stores: string[] = urlParams.get("stores").split(",");
                    stores.forEach((s: string) => paramConfig.stores.push(parseInt(s)));
                }
            }

            if (urlParams.has("orderType")) {
                switch (urlParams.get("orderType")) {
                    case "pickup":
                        paramConfig.orderType = OrderType.ORDER_AHEAD;
                        break;
                    case "delivery":
                        paramConfig.orderType = OrderType.ORDER_DELIVERY;
                        break;
                    case "table":
                        paramConfig.orderType = OrderType.ORDER_AT_TABLE;
                        break;
                }
            }

            if (urlParams.has("tableLookupCode")) {
                paramConfig.tableLookupCode = urlParams.get("tableLookupCode");
            } else if (urlParams.has("tableNumber")) {
                paramConfig.tableLookupCode = urlParams.get("tableNumber");
            }

            paramConfig.tableOnlyMode = urlParams.has("tableOnlyMode");
            paramConfig.pickupOnlyMode = urlParams.has("pickupOnlyMode");
            paramConfig.deliveryOnlyMode = urlParams.has("deliveryOnlyMode");

            if (urlParams.has("dealCode")) {
                paramConfig.dealCode = urlParams.get("dealCode");
            }

            if (urlParams.has("deviceId")) {
                paramConfig.deviceId = urlParams.get("deviceId");
            }
            if (urlParams.has("deviceType")) {
                paramConfig.deviceType = AppDeviceType[urlParams.get("deviceType")];
            }
            if (urlParams.has("deviceSecret")) {
                paramConfig.deviceSecret = urlParams.get("deviceSecret");
            }
            if (urlParams.has("actorType")) {
                paramConfig.actorType = ActorType[urlParams.get("actorType")];
            }

            paramConfig.showExitButton = urlParams.has("showExitButton");
            paramConfig.forceMobile = urlParams.has("forceMobile");
            paramConfig.noAutoScroll = urlParams.has("noAutoScroll");

            paramConfig.traceNav = urlParams.has("traceNav");
        }

        ConfigurationModel.deviceId$.next(paramConfig.deviceId);
        ConfigurationModel.actorType$.next(paramConfig.actorType);
        await firstValueFrom(this.authService.tokensLoaded$);

        if (paramConfig.beaconId && !paramConfig.parentAppURL) {
            await this.lookupBeacon(paramConfig);
        }

        if (!!paramConfig.deviceId) {
            paramConfig.tableOnlyMode = true;
            paramConfig.tableLookupCode = undefined;
            paramConfig.orderType = undefined;
            paramConfig.pickupOnlyMode = false;
            paramConfig.deliveryOnlyMode = false;
            paramConfig.scanType = "appDevice";
        }

        if (urlParams.has("m") && window.location.href.indexOf("&m=") > -1) {

            // Strip off the m param which should be on the end
            history.replaceState("active", "", window.location.href.substring(0, window.location.href.indexOf("&m=")));

            void this.identityModel.loginAsExternalMember(urlParams.get("m"));
        }

        console.log("Config Params", paramConfig);
        this.setParamConfig(paramConfig);
    }

    /**
     * Returns the current date truncated to the day (time set to 00:00:00).
     */
    private getTodayDate(): Date {
        const currentDateTime: Date = this._defaultDateTime() || new Date();
        const truncatedDate: Date = new Date(currentDateTime);
        truncatedDate.setHours(0, 0, 0, 0);
        return truncatedDate;
    }

    public async getParamConfig(reinit: boolean = false): Promise<ParamConfig> {
        if (reinit) {
            await this.init();
        }
        return firstValueFrom(this.paramConfig$);
    }

    public setParamConfig(paramConfig: ParamConfig): void {
        this.actorType.set(paramConfig.actorType);
        this._noAutoScroll.set(paramConfig.noAutoScroll);
        this._showExitButton.set(paramConfig.showExitButton);
        this._defaultDateTime.set(paramConfig.defaultLocalDateTime ? EntityData.parseLocalDateTime(paramConfig.defaultLocalDateTime) : undefined);
        this.paramConfig$.next(paramConfig);
    }

    public async loadApp(force: boolean = false): Promise<void> {
        if (this.app() && !force) {
            return;
        }
        const appId: number = await this.localConfigurationService.getNumber(Local.APP_ID);
        await this.appEntityModel.loadEntity(this._previewToken() ? appId + "_" + this._previewToken() : appId);
        if (this.appEntityModel.dataSignal()?.clientId) {
            this._clientId.set(this.appEntityModel.dataSignal()?.clientId);
        }
        this.app.set(this.appEntityModel.dataSignal());
    }

    public get now(): Date {
        return this._defaultDateTime() || new Date();
    }

    /**
     * Responds to events originating from WBO sent against the embedded iframe to refresh the App
     */
    public refreshPreview(event: MessageEvent): void {
        if (this._previewToken() && event?.data?.toString().startsWith("preview-refresh:")) {
            console.log("preview refresh");
            this._previewToken.set(event.data.toString().substring("preview-refresh:".length));
            this.localConfigurationService.saveString(Local.LOGIN_TOKEN, this._previewToken(), true);
            // Load the App in preview mode, which includes some preview data
            void this.loadApp(true);
        }
    }

    private async lookupBeacon(config: ParamConfig): Promise<void> {

        // Two tries at the beacon lookup in case a refresh token is rejected
        for (let attempt: number = 1; attempt <= 2; attempt++) {
            try {
                const loggedIn: boolean = this.authService.isLoggedIn();
                if (!loggedIn) {
                    console.log("Performing temporary login for beacon lookup");

                    // Temporarily login so the beacon data can be retrieved
                    const password: string = await this.localConfigurationService.getString(Local.LOGIN_TOKEN);
                    const appId: number = await this.localConfigurationService.getNumber(Local.APP_ID);
                    await firstValueFrom(this.authService.login("fedelta-app-token", password, LoginType.GUEST, {
                        appId: appId
                    }));
                }
                if (this.authService.isLoggedIn()) {
                    const beacon: BeaconData = await this.beaconSearchModel.lookupBeacon(config.beaconId);
                    if (beacon) {
                        config.exclusiveStore = beacon.appStore;
                        config.exclusiveStoreGroup = beacon.appStoreGroup;
                        config.tableLookupCode = beacon.tableLookupCode;
                        config.tableOnlyMode = beacon.orderType === BeaconOrderType.TABLE_ONLY || beacon.orderType === BeaconOrderType.TABLE_OR_PICKUP_ONLY;
                        config.pickupOnlyMode = beacon.orderType === BeaconOrderType.PICKUP_ONLY || beacon.orderType === BeaconOrderType.TABLE_OR_PICKUP_ONLY;
                        config.dealCode = beacon.dealCode;
                    } else {
                        config.unknownBeacon = true;
                    }
                    config.beaconScanTime = Date.now();
                }

                if (!loggedIn) {
                    console.log("Logging out after beacon lookup");
                    this.authService.logout();
                }
                return;
            } catch (e) {
                console.error(`Beacon lookup failed (attempt ${attempt})`, e);
            }
        }
        console.error(`Failed to setup config for beacon ${config.beaconId}`);
        config.beaconLookupFailed = true;
    }

    /**
     * Builds the URL to route to the specified external store
     * @param store The external store
     * @param orderType Current selected order type
     */
    public async getURLForExternalStore(store: StoreData, orderType: OrderType): Promise<string> {
        let url: string;
        const config: ParamConfig = await this.getParamConfig();
        const externalStoreConfig: ParamConfig = config.cloneForExternalStore();
        if (store.parentAppStore) {
            url = config.parentAppURL;
            externalStoreConfig.store = store.externalStoreId;
        } else {
            url = store.externalAppURL;
            externalStoreConfig.parentAppURL = config.parentAppURL ? config.parentAppURL : window.location.href.split("?")[0];
            externalStoreConfig.store = store.externalStoreId;
        }
        if (orderType) {
            externalStoreConfig.orderType = orderType;
        }

        return url + (url.indexOf("?") === -1 ? "?" : "&") + "c=" + btoa(JSON.stringify(externalStoreConfig));
    }

    /**
     * Builds the URL to return to the parent app
     */
    public async getURLForParentApp(): Promise<string> {
        let url: string;
        const config: ParamConfig = await this.getParamConfig();
        const parentAppConfig: ParamConfig = config.cloneForExternalStore();
        url = config.parentAppURL;
        parentAppConfig.store = undefined;
        return url + (url.indexOf("?") === -1 ? "?" : "&") + "c=" + btoa(JSON.stringify(parentAppConfig));
    }
}
